import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from "react";
import { Form, Select, message } from "antd";
import { captureException } from "@sentry/nextjs";
import { nanoid } from "nanoid";
import InfyButton from "../../../InfyButton/InfyButton";
import getQuestionComponent from "../../getQuestionComponent";
import { buildResponses } from "../../formUtils";
import { fetchLearnerResponsesForAdmin, getLearnerResponseByFormId, getTenantAcl, searchCompanies, searchRoles, submitLearnerResponse } from "../../apis";
import { resolveSubmitErrorMessage, V2FormData, V2Question } from "../../helper";
import { useMasterListSearch, withCustomOption } from "./useMasterListSearch";
import JobFormSkeleton from "./JobFormSkeleton";
import JobFormCompleted from "./JobFormCompleted";
import SubmissionPreview, { SubmissionData } from "./SubmissionPreview";
import TenantLogo from "../TenantLogo/TenantLogo";
import { readTenantLogo } from "../TenantLogo/useTenantLogo";
import styles from "./JobForm.module.scss";

// Dedicated field names for the always-present Company / Job Title selects. Prefixed so they never
// collide with a question UUID in the antd form values.
const COMPANY_FIELD = "__companyId";
const JOB_TITLE_FIELD = "__jobTitleId";

interface Selection {
  id: string;
  name: string;
}

interface JobFormProps {
  formData: V2FormData;
  formId: string;
  parameters: any;
}

/**
 * Dedicated layout for job-form surveys (SURVEY + settings.isJobForm).
 *
 * A primary-2 background with a curved white card. Company & Job Title selects sit at the top;
 * everything the admin built in the form builder renders beneath them. Company/Job Title are sent
 * as `jobFormMeta` (both id and label) — separate from the question `responses[]`.
 */
const JobForm: FunctionComponent<JobFormProps> = ({ formData, formId, parameters }) => {
  const [form] = Form.useForm();
  const rootRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [btnLoading, setBtnLoading] = useState({ submitBtn: false });
  // Returning-user gate: the user's existing completed submission for this form (null = none).
  const [existingChecked, setExistingChecked] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState<SubmissionData | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Company & Job Title: debounced server-side search against the master-list API.
  const company = useMasterListSearch(searchCompanies);
  const role = useMasterListSearch(searchRoles);
  // Track the chosen option's label so jobFormMeta can carry both id and display name.
  const [companySel, setCompanySel] = useState<Selection | null>(null);
  const [jobTitleSel, setJobTitleSel] = useState<Selection | null>(null);
  // Latest typed search term per field — used to offer a free-text ("use what you typed") option
  // when the learner's company / job title isn't in the master list.
  const [companyTerm, setCompanyTerm] = useState("");
  const [jobTitleTerm, setJobTitleTerm] = useState("");
  // Tenant branding + "home" destination, from the tenant ACL. Home defaults to the dashboard until loaded.
  const [companyLogo, setCompanyLogo] = useState<string>("");
  const [homeTab, setHomeTab] = useState<{ link: string; name: string }>({ link: "/learner/my-dashboard", name: "Dashboard" });

  // Job forms are learner-only (no admin fill-on-behalf). `hideButtons` is set for both the builder
  // preview AND the admin "view a student's response" drawer — a `submissionId` distinguishes the latter.
  const { componentId, sessionid, relatedModelName, hideHeaders, hideButtons = false, submissionId } = parameters;
  // Admin viewing one student's submission (read-only prefilled) vs. an empty builder preview.
  const isAdminView = Boolean(submissionId);
  const isBuilderPreview = hideButtons && !isAdminView;
  // The student's submission to render read-only in the admin view.
  const [adminSubmission, setAdminSubmission] = useState<SubmissionData | null>(null);

  const questions: V2Question[] = useMemo(() => [...(formData?.questions || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [formData]);

  // On load, check whether this learner already submitted this form → show the result screen instead
  // of the form.
  useEffect(() => {
    // Builder preview (hideButtons, no submissionId): skip any fetch so the empty form renders.
    if (isBuilderPreview) {
      setExistingChecked(true);
      return undefined;
    }
    let active = true;
    const load = async () => {
      try {
        if (isAdminView) {
          // Admin viewing a specific student's submission → render it read-only + prefilled.
          const res = await fetchLearnerResponsesForAdmin(formId, submissionId);
          if (active && res?.data) setAdminSubmission(res.data as SubmissionData);
        } else {
          // Learner returning-user gate → show the result screen unless the server EXPLICITLY says the
          // learner can submit again (`canSubmit === true`, driven by allowResubmission). Anything else —
          // canSubmit false, or absent (e.g. this renderer deployed ahead of the backend) — keeps the
          // "already submitted" screen, so we never show a fresh form the backend would refuse. When
          // resubmission is allowed the learner gets a fresh form and can share another company/role.
          // The post-submit success screen is unaffected (driven by `submitted`).
          const res = await getLearnerResponseByFormId(formId);
          const existing = res?.data?.existingSubmission as SubmissionData | undefined;
          if (active && existing?.status === "COMPLETED" && res?.data?.canSubmit !== true) setExistingSubmission(existing);
        }
      } catch {
        // if the check fails, fall through to showing the form
      } finally {
        if (active) setExistingChecked(true);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [formId, isBuilderPreview, isAdminView, submissionId]);

  // Tenant ACL → logo + the first nav tab (used as the post-submit "home" destination).
  useEffect(() => {
    let active = true;
    getTenantAcl()
      .then(acl => {
        if (!active) return;
        const logo = readTenantLogo(acl);
        if (logo) setCompanyLogo(logo);
        const tabs = acl.tenantAcl?.aclCMS?.tabs;
        if (Array.isArray(tabs) && tabs.length) {
          const first = [...tabs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))[0];
          if (first?.link) setHomeTab({ link: first.link, name: first.name || "Home" });
        }
      })
      .catch(() => {
        // non-fatal: keep the default home destination and no logo
      });
    return () => {
      active = false;
    };
  }, []);

  // Keep the iframe host sized to the content.
  useEffect(() => {
    const sendHeight = () => {
      const height = rootRef.current?.getBoundingClientRect().height;
      if (height) window.parent.postMessage({ type: "iframeResize", height }, "*");
    };
    sendHeight();
    const observer = new ResizeObserver(sendHeight);
    if (rootRef.current) observer.observe(rootRef.current);
    return () => observer.disconnect();
  }, [submitted, existingSubmission, existingChecked, previewOpen]);

  const buildJobFormMeta = (values: Record<string, unknown>) => {
    const companyId = values[COMPANY_FIELD] as string | undefined;
    const jobTitleId = values[JOB_TITLE_FIELD] as string | undefined;
    return {
      companyId,
      companyName: companySel?.id === companyId ? companySel?.name : undefined,
      jobTitleId,
      jobTitle: jobTitleSel?.id === jobTitleId ? jobTitleSel?.name : undefined
    };
  };

  const onFinish = async (values: Record<string, unknown>) => {
    const responses = buildResponses(questions, values);
    const jobFormMeta = buildJobFormMeta(values);
    setBtnLoading(prev => ({ ...prev, submitBtn: true }));
    try {
      await submitLearnerResponse(formId, { responses, componentId, sessionId: sessionid, relatedModelName, jobFormMeta });
      // Fetch the persisted submission so the result screen can offer a read-only preview.
      try {
        const res = await getLearnerResponseByFormId(formId);
        setExistingSubmission((res?.data?.existingSubmission as SubmissionData) || null);
      } catch {
        // non-fatal: submission succeeded, preview just won't be available
      }
      setSubmitted(true);
      window?.parent?.postMessage({ action: "formSubmitted" }, "*");
    } catch (err) {
      captureException(err);
      // Surface the server's own message for expected client-side rejections (4xx) instead of a generic toast.
      message.error(resolveSubmitErrorMessage(err));
      window?.parent?.postMessage({ action: "somethingWentWrong" }, "*");
    } finally {
      setBtnLoading(prev => ({ ...prev, submitBtn: false }));
    }
  };

  // Navigate the whole page (not just the embedding iframe) to the tenant's first nav tab.
  const handleGoHome = () => {
    const homeUrl = `${window.location.origin}${homeTab.link}`;
    const target = window.top || window.parent || window;
    target.location.href = homeUrl;
  };

  // Reset to a blank form so the learner can share another experience (a different company/role/stage)
  // without leaving. A job form is inherently multi-response (the backend dedups on company·role·stage),
  // so the option is always offered here; other layouts still gate it behind `allowResubmission`.
  const settings = formData?.settings as { allowResubmission?: boolean; isJobForm?: boolean } | undefined;
  const canResubmit = Boolean(settings?.allowResubmission) || Boolean(settings?.isJobForm);
  const handleSubmitAnother = () => {
    form.resetFields();
    setCompanySel(null);
    setJobTitleSel(null);
    setExistingSubmission(null);
    setSubmitted(false);
    if (typeof window !== "undefined") window.scrollTo({ top: 0 });
  };

  // While checking for an existing submission, show the job-form skeleton (avoids flashing the form).
  if (!existingChecked) {
    return <JobFormSkeleton />;
  }

  // Admin viewing a student's response — render it read-only + prefilled (no submit, no result screen).
  if (isAdminView) {
    return (
      <div className={styles.jobFormBackground}>
        <div className={styles.content}>
          <TenantLogo src={companyLogo} />
          <div className={styles.card}>
            {!hideHeaders && (parameters?.formtitle || formData?.title) && (
              <div className={styles.header}>
                <h1 className={styles.title}>{parameters?.formtitle || formData?.title}</h1>
              </div>
            )}
            {adminSubmission ? (
              <div className={styles.fieldsScroll}>
                <SubmissionPreview inline questions={questions} submission={adminSubmission} />
              </div>
            ) : (
              <p className="m-0 text-sm text-neutral-8">No response found.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Result screen — shown right after submit AND when the user returns to an already-submitted form.
  if (submitted || existingSubmission) {
    return (
      <div className={styles.jobFormBackground}>
        <div className={styles.content}>
          <TenantLogo src={companyLogo} />
          <div className={styles.card}>
            <JobFormCompleted
              action={
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {existingSubmission?.responses?.length ? (
                    <InfyButton type="ghost" bicolor="primary" onClick={() => setPreviewOpen(true)}>
                      Preview your response
                    </InfyButton>
                  ) : null}
                  {submitted && canResubmit ? (
                    <InfyButton type="ghost" bicolor="primary" onClick={handleSubmitAnother}>
                      Submit another response
                    </InfyButton>
                  ) : null}
                  <InfyButton type="primary" bicolor="primary" onClick={handleGoHome}>
                    Go to {homeTab.name}
                  </InfyButton>
                </div>
              }
            />
            <SubmissionPreview open={previewOpen} onClose={() => setPreviewOpen(false)} questions={questions} submission={existingSubmission} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={rootRef} className={styles.jobFormBackground}>
      <div className={styles.content}>
        <TenantLogo src={companyLogo} />
        <div className={styles.card}>
          {!hideHeaders && (
            <div className={styles.header}>
              {(parameters?.formtitle || formData?.title) && <h1 className={styles.title}>{parameters?.formtitle || formData?.title}</h1>}
              {formData?.description && <p className={styles.description}>{formData.description}</p>}
            </div>
          )}

          <Form form={form} layout="vertical" requiredMark={false} onFinish={onFinish} scrollToFirstError className={styles.form}>
            {/* Only the fields scroll; the footer below stays pinned. */}
            <div className={styles.fieldsScroll}>
              {/* Always-present job fields — Company & Job Title side by side (stacked on mobile) */}
              <div className={styles.jobFields}>
                <Form.Item
                  className={styles.jobField}
                  name={COMPANY_FIELD}
                  label={
                    <span>
                      Company<span className="text-[#FF3B3B] ml-1 text-xl font-semibold">*</span>
                    </span>
                  }
                  rules={[{ required: true, message: "Please select a company" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select or type your company"
                    filterOption={false}
                    onSearch={term => {
                      setCompanyTerm(term);
                      company.onSearch(term);
                    }}
                    loading={company.loading}
                    options={withCustomOption(company.options, companyTerm)}
                    notFoundContent={company.loading ? "Searching…" : "Type to add your company"}
                    onChange={(value, option) => {
                      setCompanyTerm("");
                      setCompanySel(value ? { id: value as string, name: (option as { label?: string })?.label || "" } : null);
                    }}
                  />
                </Form.Item>

                <Form.Item
                  className={styles.jobField}
                  name={JOB_TITLE_FIELD}
                  label={
                    <span>
                      Job Title<span className="text-[#FF3B3B] ml-1 text-xl font-semibold">*</span>
                    </span>
                  }
                  rules={[{ required: true, message: "Please select a job title" }]}
                >
                  <Select
                    showSearch
                    placeholder="Select or type your job title"
                    filterOption={false}
                    onSearch={term => {
                      setJobTitleTerm(term);
                      role.onSearch(term);
                    }}
                    loading={role.loading}
                    options={withCustomOption(role.options, jobTitleTerm)}
                    notFoundContent={role.loading ? "Searching…" : "Type to add your job title"}
                    onChange={(value, option) => {
                      setJobTitleTerm("");
                      setJobTitleSel(value ? { id: value as string, name: (option as { label?: string })?.label || "" } : null);
                    }}
                  />
                </Form.Item>
              </div>

              {/* Admin-built questions */}
              {questions.map(question => (
                <div className={styles.formComponents} key={question.id || nanoid()}>
                  {getQuestionComponent({ question, formType: formData?.type, legacyId: formData?.metadata?.legacyId })}
                </div>
              ))}
            </div>

            {/* Sticky footer — hidden in builder preview */}
            {!hideButtons && (
              <div className={styles.footer}>
                <InfyButton type="primary" bicolor="primary" htmlType="submit" loading={btnLoading.submitBtn}>
                  Submit
                </InfyButton>
              </div>
            )}
          </Form>
        </div>
      </div>
    </div>
  );
};

export default JobForm;
